<template>
  <section class="signup">
    <div class="title-intro">
      <router-link :to="{ name: 'auth'}" class="btn-back ls-float-left ls-ico-shaft-left" :title="$t('signup.main.login')"></router-link>
      <h2 class="ls-title-3">{{ $t('signup.main.create_account') }}</h2>
    </div>
    <flash-message></flash-message>
    <div class="ls-alert-danger" v-if="errorStatus">
      <strong>{{ $t('application.alert.warning') }}</strong> {{ $t('signup.main.not_possible_create_account') }}
    </div>

    <form class="ls-form row" @submit.prevent="validateBeforeSubmit" v-if="!formSubmitted">

      <transition name="fade" mode="out-in" appear>
        <fieldset class="col-md-4">
          <div class="ls-box ls-md-margin-bottom">
            <div class="title-form">
              <h5 class="ls-title-5"><i class="fa fa-user" aria-hidden="true"></i> {{ $t('signup.main.person_data') }}</h5>
            </div>
            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.name')) }">
              <b class="ls-label-text">{{ $t('signup.main.name') }}</b>
              <input
                type="text"
                v-model="signup.seller.name"
                v-validate="'required'"
                :placeholder="$t('signup.placeholder.fill_full_name')"
                :name="$t('signup.main.name')" />
              <small class="ls-help-message" v-show="errors.has($t('signup.main.name'))">{{ errors.first($t('signup.main.name')) }}</small>
            </label>

            <label class="ls-label" :class="{'ls-error': errors.has('signup.seller.contact_attributes.area_code') || errors.has($t('signup.main.phone'))}">
              <b class="ls-label-text">{{ $t('signup.main.phone_number') }}</b>
              <input
                type="tel"
                maxlength="16"
                v-model="full_phone"
                v-validate="'required|min:14'"
                v-mask="phoneMask"
                :placeholder="$t('signup.placeholder.phone')"
                :name="$t('signup.main.phone')"
              >
              <small class="ls-help-message ls-position-relative" v-show="errors.has('signup.seller.contact_attributes.area_code') || errors.has($t('signup.main.phone'))">{{ errors.first($t('signup.main.phone')) }}</small>
            </label>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.account_type')) }">
              <b class="ls-label-text">{{ $t('signup.main.account_type') }}</b>
              <label class="ls-label-text ls-float-left">
                <input
                  type="radio"
                  value="P"
                  v-model="signup.seller.kind"
                  v-validate="'required'"
                  :name="$t('signup.main.account_type')" />
                {{ $t('signup.main.personal_account') }}
              </label>

              <label class="ls-label-text ls-float-left">
                <input
                  type="radio"
                  value="C"
                  v-model="signup.seller.kind"
                  v-validate="'required'"
                  :name="$t('signup.main.account_type')" />
                {{ $t('signup.main.customer_account') }}
              </label>
              <div class="col-md-12 row">
                <small class="ls-help-message" v-show="errors.has($t('signup.main.account_type'))">{{ errors.first($t('signup.main.account_type')) }}</small>
              </div>
            </label>

            <div id="business-account" v-if="signup.seller.kind === 'C'">
              <div class="title-form">
                <h5 class="ls-title-5"><i class="fa fa-building-o" aria-hidden="true"></i> {{ $t('signup.main.company_data') }}</h5>
              </div>
              <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.fantasy_name')) && signup.seller.kind === 'C' }">
                <b class="ls-label-text">{{ $t('signup.main.fantasy_name') }}</b>
                <input
                  type="text"
                  v-model="signup.seller.trade_name"
                  v-validate="'required'"
                  :placeholder="$t('signup.placeholder.trade_name')"
                  :name="$t('signup.main.fantasy_name')" />
                <small class="ls-help-message" v-show="errors.has($t('signup.main.fantasy_name'))">{{ errors.first($t('signup.main.fantasy_name')) }}</small>
              </label>

              <label class="ls-label ls-position-relative" :class="{'ls-error': errors.has($t('signup.main.cnpj'))}">
                <b class="ls-label-text">{{ $t('signup.main.cnpj') }}</b>
                <input
                  type="tel"
                  maxlength="18"
                  v-mask="'99.999.999/9999-99'"
                  v-model="signup.seller.cnpj"
                  v-validate="'required|min:18|cnpj'"
                  :placeholder="$t('signup.placeholder.cnpj')"
                  :name="$t('signup.main.cnpj')" />
                <i class="fa fa-check" aria-hidden="true" v-if="signup.seller.cnpj.length && !errors.has($t('signup.main.cnpj'))"></i>
                <small class="ls-help-message" v-show="errors.has($t('signup.main.cnpj'))">{{ errors.first($t('signup.main.cnpj')) }}</small>
              </label>
            </div>
            <label v-if="signup.seller.kind === 'P'" class="ls-label ls-position-relative" :class="{'ls-error': errors.has($t('signup.main.cpf'))}">
              <b class="ls-label-text">{{ $t('signup.main.cpf') }}</b>
              <input
                type="tel"
                maxlength="14"
                v-validate="'required|min:14|cpf'"
                v-model="signup.seller.cpf"
                v-mask="'999.999.999-99'"
                :placeholder="$t('signup.placeholder.cpf')"
                :name="$t('signup.main.cpf')" />
               <i class="fa fa-check" aria-hidden="true" v-if="signup.seller.cpf.length > 0 && !errors.has($t('signup.main.cpf'))"></i>
              <small class="ls-help-message" v-if="errors.has($t('signup.main.cpf'))">{{ errors.first($t('signup.main.cpf')) }}</small>
            </label>
          </div>
        </fieldset>
      </transition>

      <transition name="fade" mode="out-in" appear>
        <fieldset class="col-md-4">
          <div class="ls-box ls-md-margin-bottom">
            <div class="title-form">
              <h5 class="ls-title-5"><i class="fa fa-map-marker" aria-hidden="true"></i> {{ $t('signup.main.address') }}</h5>
            </div>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.postal_code')) }">
              <b class="ls-label-text">{{ $t('signup.main.postal_code') }}</b>
              <input
                type="tel"
                class="form-control ls-mask-cep"
                maxlength="10"
                v-model="signup.seller.address_attributes.postal_code"
                v-validate="'required|min:10|cep'"
                v-mask="'99.999-999'"
                :placeholder="$t('signup.placeholder.postal_code')"
                :name="$t('signup.main.postal_code')" />
              <small class="ls-help-message" v-show="errors.has($t('signup.main.postal_code'))">{{ errors.first($t('signup.main.postal_code')) }}</small>
              <p class="ls-float-right"><a href="//www.buscacep.correios.com.br/sistemas/buscacep/" target="_blank">{{ $t('settings.main.forgot_zipcode') }}</a></p>
            </label>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.street')) }">
              <b class="ls-label-text">{{ $t('signup.main.street') }}</b>
              <input
                type="text"
                class="form-control"
                v-model="signup.seller.address_attributes.street"
                v-validate="'required'"
                :placeholder="$t('signup.placeholder.street_avenue')"
                :name="$t('signup.main.street')" />
              <small class="ls-help-message" v-show="errors.has($t('signup.main.street'))">{{ errors.first($t('signup.main.street')) }}</small>
            </label>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.address_number')) }">
              <b class="ls-label-text">{{ $t('signup.main.address_number') }}</b>
              <input
                type="text"
                name="signup.seller.address_attributes.number"
                class="form-control"
                v-model="signup.seller.address_attributes.number"
                v-validate="'required'"
                :placeholder="$t('signup.placeholder.number')"
                :name="$t('signup.main.address_number')" />
              <small class="ls-help-message" v-show="errors.has($t('signup.main.address_number'))">{{ errors.first($t('signup.main.address_number')) }}</small>
            </label>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.neighborhood')) }">
              <b class="ls-label-text">{{ $t('signup.main.neighborhood') }}</b>
              <input
                type="text"
                class="form-control"
                v-model="signup.seller.address_attributes.neighborhood"
                v-validate="'required'"
                :placeholder="$t('signup.placeholder.neighborhood_example')"
                :name="$t('signup.main.neighborhood')" />
              <small class="ls-help-message" v-show="errors.has($t('signup.main.neighborhood'))">{{ errors.first($t('signup.main.neighborhood')) }}</small>
            </label>

            <label class="ls-label">
              <b class="ls-label-text">{{ $t('signup.main.complement') }}</b>
              <input
                type="text"
                class="form-control"
                name="signup.seller.address_attributes.complement"
                v-model="signup.seller.address_attributes.complement"
                :placeholder="$t('signup.placeholder.optional')" />
            </label>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.city')) }">
              <b class="ls-label-text">{{ $t('signup.main.city') }}</b>
              <input
                type="text"
                class="form-control"
                v-model="signup.seller.address_attributes.city"
                v-validate="'required'"
                :placeholder="$t('signup.placeholder.your_city')"
                :name="$t('signup.main.city')" />
              <small class="ls-help-message" v-show="errors.has($t('signup.main.city'))">{{ errors.first($t('signup.main.city')) }}</small>
            </label>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.state')) }">
              <b class="ls-label-text">{{ $t('signup.main.state') }}</b>
              <div class="ls-custom-select" :class="{'custom-danger': errors.has($t('signup.main.state')) }">
                <select
                  class="ls-select"
                  v-model="signup.seller.address_attributes.state"
                  v-validate="'required'"
                  :name="$t('signup.main.state')">
                  <option value="" default>{{ $t('signup.main.choose_state') }}</option>
                  <option value="AC">AC</option>
                  <option value="AL">AL</option>
                  <option value="AP">AP</option>
                  <option value="AM">AM</option>
                  <option value="BA">BA</option>
                  <option value="CE">CE</option>
                  <option value="DF">DF</option>
                  <option value="ES">ES</option>
                  <option value="GO">GO</option>
                  <option value="MA">MA</option>
                  <option value="MS">MS</option>
                  <option value="MG">MG</option>
                  <option value="PA">PA</option>
                  <option value="PB">PB</option>
                  <option value="PR">PR</option>
                  <option value="PE">PE</option>
                  <option value="PI">PI</option>
                  <option value="RJ">RJ</option>
                  <option value="RN">RN</option>
                  <option value="RS">RS</option>
                  <option value="RO">RO</option>
                  <option value="RR">RR</option>
                  <option value="SC">SC</option>
                  <option value="SP">SP</option>
                  <option value="SE">SE</option>
                  <option value="TO">TO</option>
                </select>
              </div>
              <small class="ls-help-message" v-show="errors.has($t('signup.main.state'))">{{ errors.first($t('signup.main.state')) }}</small>
            </label>
          </div>
        </fieldset>
      </transition>

      <transition name="fade" mode="out-in" appear>
        <fieldset class="col-md-4">
          <div class="ls-box ls-md-margin-bottom">
            <div class="title-form">
              <h5 class="ls-title-5"><i class="fa fa-sign-in" aria-hidden="true"></i> {{ $t('signup.main.access_data') }}</h5>
            </div>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.mail')) }">
              <b class="ls-label-text">{{ $t('signup.main.mail') }}</b>
                <input
                  type="text"
                  class="form-control"
                  v-model="signup.seller.email"
                  v-validate="'required'"
                  :placeholder="$t('signup.placeholder.mail_example')"
                  :name="$t('signup.main.mail')" />
                <small class="ls-help-message" v-show="errors.has($t('signup.main.mail'))">{{ errors.first($t('signup.main.mail')) }}</small>
            </label>

            <label class="ls-label" :class="{'ls-error': errors.has($t('signup.main.password')) }">
              <b class="ls-label-text">{{ $t('signup.main.password') }}</b>
                <input
                  type="password"
                  class="form-control"
                  value=""
                  v-model="signup.seller.password"
                  v-validate="'required'"
                  :placeholder="$t('signup.placeholder.choose_password')"
                  :name="$t('signup.main.password')" >
                <small class="ls-help-message" v-show="errors.has($t('signup.main.password'))">{{ errors.first($t('signup.main.password')) }}</small>
            </label>

            <div class="ls-actions-btn">
              <button
                v-bind:disabled="disabled"
                class="ls-btn-primary ls-float-right ls-no-margin"
                type="submit">{{ buttonText }}
              </button>
            </div>
          </div>
        </fieldset>
      </transition>

    </form>

  </section>
</template>
<script>
  import { mapGetters, mapMutations, mapActions } from 'vuex'
  import * as format from 'src/utils/form'

  export default {
    name: 'Seller-Signup',
    data () {
      return {
        onlyNumber: /[\D]/g,
        formSubmitted: false,
        signup: {
          seller: {
            name: '',
            email: '',
            password: '',
            cpf: '',
            kind: 'P',
            cnpj: '',
            trade_name: '',
            contact_attributes: {
              area_code: '',
              number: ''
            },
            address_attributes: {
              postal_code: '',
              street: '',
              number: '',
              neighborhood: '',
              complement: '',
              city: '',
              state: ''
            }
          }
        },
        phoneMask: '(99) 9999-9999',
        full_phone: '',
        errorStatus: false,
        disabled: false,
        buttonText: this.$t('signup.main.create_account'),
        changeAddress: false
      }
    },
    computed: {
      ...mapGetters(['addressFound'])
    },
    methods: {
      ...mapMutations(['clearFlash']),
      ...mapActions(['createAccount', 'getAddress', 'loadFullSeller']),
      formatPhone () {
        this.full_phone = format.formatPhone(this.full_phone)
      },
      prepareParameters () {
        var phoneOnlyNumber = this.full_phone.replace(/\D/g, '')
        this.signup.seller.contact_attributes = {
          area_code: phoneOnlyNumber.substr(0, 2),
          number: phoneOnlyNumber.substr(2, 9)
        }
      },
      validateBeforeSubmit () {
        this.clearFlash()
        this.$validator.validateAll().then(this.afterValidate)
      },
      afterValidate (success) {
        if (!success) return
        this.prepareParameters()
        this.signUp()
      },
      signUp () {
        this.disabled = true
        this.buttonText = this.$t('signup.main.creating_account')
        this.createAccount(this.signup).then(this.afterSignup)
      },
      afterSignup (response) {
        if (response.status === 200 || response.status === 201) {
          this.reloadSeller()
        }
        this.disabled = false
        this.buttonText = this.$t('signup.main.create_account')
      },
      reloadSeller () {
        this.loadFullSeller().then(this.afterReloadSeller)
      },
      afterReloadSeller (response) {
        if (response && response.status === 200) {
          this.$router.push('/wizard')
        }
      }
    },
    watch: {
      'signup.seller.kind' () {
        if (this.signup.seller.kind === 'C') {
          this.signup.seller.cpf = ''
        }
      },
      addressFound: function (value) {
        if (!this.changeAddress) return
        this.signup.seller.address_attributes.street = value.address.street || ''
        this.signup.seller.address_attributes.number = value.address.number || ''
        this.signup.seller.address_attributes.neighborhood = value.address.neighborhood || ''
        this.signup.seller.address_attributes.complement = value.address.complement || ''
        this.signup.seller.address_attributes.city = value.address.city || ''
        this.signup.seller.address_attributes.state = value.address.state || ''
        this.changeAddress = false
        this.$loader.hide()
      },
      'signup.seller.address_attributes.postal_code': function (newVal, oldVal) {
        this.changeAddress = newVal !== oldVal
        if (newVal.length === 10) this.$loader.show()
      },
      full_phone: function (newVal) {
        if (this.full_phone !== undefined && this.full_phone.replace(this.onlyNumber, '').length > 10) {
          this.phoneMask = '(99) 9 9999-9999'
        }
      }
    }
  }
</script>
<style lang="scss" scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.1s;
  }
  .fade-enter, .fade-leave-active {
    opacity: 0;
  }
  section.signup {
    position: relative;
    top: 0;
    left: 0;
    padding: 20px;
    width: 100%;
    display: inline-block;
  }
  .title-intro {
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 40px;
    padding-top: 20px;
  }
  $bg-color: #ccc;
  .btn-back {
    background: darken($bg-color, 10%);
    cursor: pointer;
    padding: 10px;
    border-radius: 20px;
    color: #fff;
    position: relative;
    top: -8px;
    margin-right: 10px;
    &:hover {
      background: darken($bg-color, 20%);
    }
  }
  .title-form {
    display: block;
    border-bottom: 1px solid #dddddd;
    margin: 15px 0;
  }
  label > small {
    position: absolute;
    color: #868686;
  }
  .custom-danger {
    border: 1px solid #d75553;
  }
  .ls-actions-btn {
    margin: 15px 0;
  }
  .ls-label {
    margin-bottom: 28px;
  }
  .fa.fa-check {
    display: block;
    &:before {
      position: absolute;
      right: 10px;
      top: 27px;
      color: #55b87a;
    }
  }
</style>



// WEBPACK FOOTER //
// Main.vue?695a621a